// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { WireContourService } from '@3d-scene/common/util/wire-contour-service';
import { ArticleUsageVO, ArticleVO } from '../model/vo/article.vo';
import { ArticleManager } from '../manager/article.manager';
import { ArticleService } from './article.service';
import { GlobalContainer } from '@3d-scene/model3d/global-container';
import { Dictionary } from 'typescript-collections';
import { Object3D } from 'three';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUsageUtils } from '../util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '../constants/article-usage-info-property-def';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ProjectModel } from '@project/model/project.model';
import { DrillingHoleInfo } from '@3d-scene/model3d/util/drillinghole/drilling-hole-info';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { DrillingHoleUtils } from '@3d-scene/model3d/util/drillinghole/drilling-hole-utils';
import { CompositeArticleInfoUtils } from '../util/composite-article-info-utils';
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';

@Injectable({
  providedIn: 'root'
})
export class ArticleUsageService {

  constructor(private projectModel: ProjectModel,
              private articleManager: ArticleManager,
              private articleService: ArticleService,
              private compositeArticleInfoUtils: CompositeArticleInfoUtils,
              private globalContainer: GlobalContainer) { }

  public getArticleOrCompositeArticleByArticleUsage(articleUsage: ArticleUsageVO): ArticleVO | null {
    if (!articleUsage) {
      return null;
    }

    if (ArticleUsageUtils.isCompositePartArticleUsage(articleUsage)) {
      return this.compositeArticleInfoUtils.getCompositeArticleFromUsage(articleUsage);
    }
    return this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
  }

  public isArticleUsageDeletable(articleUsage: ArticleUsageVO): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.articleService.isArticleDeletableById(articleUsage.articleId, articleUsage.articleVersion);
  }

  public getArticleByArticleUsage(articleUsage: ArticleUsageVO,): ArticleVO {
    if (!articleUsage) {
      return null;
    }
    return this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
  }

  public isCustomArticleUsage(articleUsage: ArticleUsageVO): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.articleService.isCustomArticleById(articleUsage.articleId, articleUsage.articleVersion);
  }
  public isMountingRailArticleUsage(articleUsage: ArticleUsageVO | undefined): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.articleService.isMountingRailById(articleUsage.articleId, articleUsage.articleVersion);
  }

  public isDrillingHoleArticleUsage(articleUsage: ArticleUsageVO | undefined): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.articleService.isDrillingHoleById(articleUsage.articleId, articleUsage.articleVersion);
  }

  public isRailPropertiesTabEnabled(articleUsage: ArticleUsageVO): boolean {
    if (!articleUsage) {
      return false;
    }
    const isSpeedwayOrFreeMounting: Boolean =
      this.articleService.isSpeedwayById(articleUsage.articleId, articleUsage.articleVersion) ||
      this.articleService.isFreeMountingById(articleUsage.articleId, articleUsage.articleVersion);
    return !isSpeedwayOrFreeMounting;
  }

  public isZoneArticleUsage(articleUsage: ArticleUsageVO): boolean {
    return this.isBlockingZoneArticleUsage(articleUsage) || this.isEmptyZoneArticleUsage(articleUsage);
  }

  public isBlockingZoneArticleUsage(articleUsage: ArticleUsageVO): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.articleService.isBlockingZoneById(articleUsage.articleId, articleUsage.articleVersion);
  }

  public isEmptyZoneArticleUsage(articleUsage: ArticleUsageVO): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.articleService.isEmptyZoneArticleById(articleUsage.articleId, articleUsage.articleVersion);
  }

  public isCoverLabelStripArticleUsage(articleUsage: ArticleUsageVO): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.articleService.isCoverLabelStripArticleById(articleUsage.articleId, articleUsage.articleVersion);
  }

  public isSpeedwayArticleUsage(articleUsage: ArticleUsageVO): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.articleService.isSpeedwayById(articleUsage.articleId, articleUsage.articleVersion);
  }

  public isWireBridgeArticleUsage(articleUsage: ArticleUsageVO | undefined): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.articleService.isWireBridgeById(articleUsage.articleId, articleUsage.articleVersion);
  }

  public isBusbarOrCoverArticleUsage(articleUsage: ArticleUsageVO): boolean {
    if (!articleUsage) {
      return false;
    }
    return (
      this.articleService.isBusbarArticleById(articleUsage.articleId, articleUsage.articleVersion) ||
      this.articleService.isCoverArticleById(articleUsage.articleId, articleUsage.articleVersion)
    );
  }

  public isIO_750_753ArticleUsage(articleUsage: ArticleUsageVO): boolean {
    if (!articleUsage) {
      return false;
    }
    return this.articleService.isIO_750_753ArticleById(articleUsage.articleId, articleUsage.articleVersion);
  }

  public updateDrillingHoleGeometricData(
    articleUsages: ArticleUsageVO[]
  ): void {
    const guidToDataMapping: Dictionary<string, any> = ArticleContainerUtils.collectContainersGeometricData(
      this.globalContainer,
      function (object: Object3D): boolean {
        return !(
          object instanceof Connection
          || object.name == WireContourService.WIRECONTOUR_NAME
          || IsThreeJsType.lineSegments(object)
        );
      }
    );
    for (const usage of articleUsages) {
      if (!this.isDrillingHoleArticleUsage(usage)) {
        continue;
      }

      const geometricData: any = guidToDataMapping.getValue(usage.id);
      if (geometricData) {
        if (geometricData.position) {
          ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
            usage,
            ArticleUsageInfoPropertyDef.ARTICLE_POSITION_X,
            Boolean(geometricData.position.x) ? -geometricData.position.x : 0
          );
          ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
            usage,
            ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Y,
            Boolean(geometricData.position.y) ? geometricData.position.y : 0
          );
          ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
            usage,
            ArticleUsageInfoPropertyDef.ARTICLE_POSITION_Z,
            Boolean(geometricData.position.z) ? geometricData.position.z : 0
          );
          ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
            usage,
            ArticleUsageInfoPropertyDef.ARTICLE_OFFSET,
            Boolean(geometricData.position.x) ? -geometricData.position.x : 0
          );
        }

        if (geometricData.rotationX) {
          ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(usage, ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_X, geometricData.rotationX);
        }
        if (geometricData.rotationY) {
          ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(usage, ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_Y, -geometricData.rotationY);
        }
        if (geometricData.rotationZ) {
          ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(usage, ArticleUsageInfoPropertyDef.ARTICLE_ROTATION_Z, -geometricData.rotationZ);
        }
      }
    }
  }

  public getUsedDrillingHoleOffset(mountingRailArticleUsage: ArticleUsageVO): number {
    if (!mountingRailArticleUsage) {
      return 0;
    }
    const drillingHoleObjects: DrillingHoleInfo[] = [];
    const childGUIDs: string[] = ArticleUsageUtils.getAcceptingAreaIds(mountingRailArticleUsage);
    for (const guid of childGUIDs) {
      const childUsage: ArticleUsageVO = this.projectModel.getArticleUsage(guid);
      if (this.isDrillingHoleArticleUsage(childUsage)) {
        const childArticle: ArticleVO = this.getArticleByArticleUsage(childUsage);
        if (childArticle) {
          const drillingHoleInfo: DrillingHoleInfo = new DrillingHoleInfo();
          drillingHoleInfo.usageId = childUsage.id;
          drillingHoleInfo.position = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
            childUsage,
            ArticleUsageInfoPropertyDef.ARTICLE_OFFSET
          );
          drillingHoleInfo.shapeType = DrillingHoleUtils.getDrillingHoleShapeTypeByArticle(childArticle);
          drillingHoleInfo.size = DrillingHoleUtils.getDrillingHoleSizeByArticle(childArticle);
          drillingHoleObjects.push(drillingHoleInfo);
        }
      }
    }
    DrillingHoleUtils.sortDrillingHolesByPosition(drillingHoleObjects);
    let offset: number = 0;
    if (drillingHoleObjects.length > 0 && drillingHoleObjects[0]) {
      const drillingHoleInfo: DrillingHoleInfo = drillingHoleObjects[0];
      offset = UnitConverter.fromToRounded('3d', 'mm', drillingHoleInfo.position);
    }
    return offset;
  }

  public getParentArticleUsages(articleUsage: ArticleUsageVO): ArticleUsageVO[] {
    const parentUsageIds: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(articleUsage);
    return parentUsageIds.map(usageId => this.projectModel.getArticleUsage(usageId));
  }
}
